
import { api } from './base';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';

export const DIRECTORY_COLUMN_TYPES = ['text', 'number', 'date', 'boolean', 'enum'] as const;
export type DirectoryColumnType = (typeof DIRECTORY_COLUMN_TYPES)[number];

export const DIRECTORY_COLUMN_TYPE_LABELS: Record<DirectoryColumnType, string> = {
  text: 'Текст',
  number: 'Число',
  date: 'Дата',
  boolean: 'Да/нет',
  enum: 'Список',
};

export interface DirectoryColumn {
  key: string;
  type: DirectoryColumnType;
  label: string;
  required?: boolean;
  internal?: boolean;
  options?: { value: string; label: string }[];
  archivedAt?: string | null;
}

export type DirectoryVisibility = 'public' | 'linked';

export interface Directory {
  id: string;
  name: string;
  description: string | null;
  code: string | null;
  parentDirectoryId: string | null;
  parentDirectoryName?: string | null;
  visibility: DirectoryVisibility;
  columns: DirectoryColumn[];
  displayColumn: string | null;
  subtitleColumn: string | null;
  weightColumn: string | null;
  entryCount?: number;
  orphanCount?: number;
}

export type DirectoryAttrs = Record<string, string | number | boolean | null>;

export interface DirectoryEntry {
  id: string;
  code: string | null;
  label: string;
  subtitle: string | null;
  parentEntryId: string | null;
  archivedAt: string | null;
  attrs: DirectoryAttrs;
}

export interface DirectoryOption {
  id: string;
  code: string | null;
  label: string;
  subtitle: string | null;
  parentEntryId: string | null;
}

export interface DirectoryEntryCard {
  id: string;
  code: string | null;
  directoryName: string;
  archivedAt: string | null;
  columns: { key: string; label: string; type: DirectoryColumnType; internal: boolean }[];
  attrs: DirectoryAttrs;
  parent: { id: string; directoryName: string; label: string } | null;
}

export interface DirectoryInput {
  name?: string;
  description?: string | null;
  code?: string | null;
  parentDirectoryId?: string | null;
  visibility?: DirectoryVisibility;
  columns?: DirectoryColumn[];
  displayColumn?: string | null;
  subtitleColumn?: string | null;
  weightColumn?: string | null;
}

export interface EntryInput {
  id?: string;
  code?: string | null;
  parentEntryId?: string | null;
  attrs?: Record<string, unknown>;
}

export interface ImportRowResult {
  line: number;
  action: 'create' | 'update' | 'error';
  label: string;
  error?: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  errors: number;
  rows: ImportRowResult[];
  unknownHeaders: string[];
}

export interface EntryListFilters {
  search?: string;
  parentEntryId?: string;
  orphans?: boolean;
  archived?: boolean;
  limit?: number;
  offset?: number;
}

function entriesQuery(filters: EntryListFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.parentEntryId) params.set('parentEntryId', filters.parentEntryId);
  if (filters.orphans) params.set('orphans', 'true');
  if (filters.archived) params.set('archived', 'true');
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const directoriesApi = {
  list: () => api.get<Directory[]>('/api/v1/admin/directories'),
  get: (id: string) => api.get<Directory>(`/api/v1/admin/directories/${id}`),
  create: (dto: DirectoryInput) => api.post<Directory>('/api/v1/admin/directories', dto),
  update: (id: string, dto: DirectoryInput) =>
    api.patch<Directory>(`/api/v1/admin/directories/${id}`, dto),
  remove: (id: string) => api.delete<void>(`/api/v1/admin/directories/${id}`),

  saveColumns: (
    id: string,
    dto: {
      columns: DirectoryColumn[];
      displayColumn?: string | null;
      subtitleColumn?: string | null;
      weightColumn?: string | null;
    },
  ) => api.put<Directory>(`/api/v1/admin/directories/${id}/columns`, dto),

  columnUsage: (id: string, key: string) =>
    api.get<{ count: number }>(`/api/v1/admin/directories/${id}/columns/${key}/usage`),

  dropColumn: (id: string, key: string) =>
    api.delete<{ affected: number }>(`/api/v1/admin/directories/${id}/columns/${key}`),

  entries: (id: string, filters: EntryListFilters = {}) =>
    api.get<DirectoryEntry[]>(`/api/v1/admin/directories/${id}/entries${entriesQuery(filters)}`),

  saveEntries: (id: string, entries: EntryInput[]) =>
    api.patch<DirectoryEntry[]>(`/api/v1/admin/directories/${id}/entries`, { entries }),

  bulk: (id: string, action: 'archive' | 'unarchive' | 'delete', ids: string[]) =>
    api.post<{ affected: number }>(`/api/v1/admin/directories/${id}/entries/bulk`, { action, ids }),

  removeEntry: (id: string, entryId: string, force = false) =>
    api.delete<{ orphaned: number }>(
      `/api/v1/admin/directories/${id}/entries/${entryId}${force ? '?force=true' : ''}`,
    ),

  import: (id: string, text: string, preview: boolean) =>
    api.post<ImportResult>(`/api/v1/admin/directories/${id}/import`, { text, preview }),

  downloadExport: async (id: string, fileName: string): Promise<void> => {
    const token = await getValidToken();
    const res = await fetch(`${config.apiUrl}/api/v1/admin/directories/${id}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) throw new Error('Не удалось выгрузить справочник');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  },

  options: (id: string) => api.get<DirectoryOption[]>(`/api/v1/directories/${id}/entries`),

  card: (directoryId: string, entryId: string, ticketId?: string) =>
    api.get<DirectoryEntryCard>(
      `/api/v1/directories/${directoryId}/entries/${entryId}${ticketId ? `?ticketId=${ticketId}` : ''}`,
    ),
};
